import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/layout/Header";
import { SEO } from "@/components/seo/SEO";

const CompanyRegistration = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    sector: "",
    employeeCount: "",
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const sectorKeys = ['technology', 'health', 'finance', 'education', 'commerce', 'industry', 'services', 'other'];
  const employeeCounts = ["1-10", "11-50", "51-200", "201-500", "500+"];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: { first_name: formData.firstName, last_name: formData.lastName }
        }
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        let profileData = null;
        const { data: existingProfile } = await supabase.from('profiles').select('id').eq('user_id', authData.user.id).maybeSingle();
        
        if (existingProfile) {
          profileData = existingProfile;
          await supabase.from('profiles').update({ first_name: formData.firstName, last_name: formData.lastName }).eq('user_id', authData.user.id);
        } else {
          const { data: newProfile, error: createError } = await supabase.from('profiles').insert({
            user_id: authData.user.id,
            email: authData.user.email || formData.email,
            first_name: formData.firstName,
            last_name: formData.lastName
          }).select('id').single();
          if (createError) throw createError;
          profileData = newProfile;
        }
        
        if (!profileData) throw new Error(t('companyRegistration.toast.profileError'));

        await supabase.from('company_requests').insert({
          company_id: null,
          requester_id: profileData.id,
          company_name: formData.companyName,
          company_sector: formData.sector,
          company_employee_count: parseInt(formData.employeeCount.split('-')[0]) || 500,
          status: 'pending'
        });

        try {
          await supabase.functions.invoke('send-company-registration-emails', {
            body: { companyName: formData.companyName, companyEmail: formData.email, firstName: formData.firstName, lastName: formData.lastName }
          });
        } catch (emailError) {
          console.error('Failed to send emails:', emailError);
        }

        toast({ title: t('companyRegistration.toast.success'), description: t('companyRegistration.toast.successDesc') });
        await supabase.auth.signOut();
        navigate('/');
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: t('companyRegistration.toast.error'), description: error.message || t('companyRegistration.toast.errorDesc') });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO title={t('companyRegistration.seoTitle')} description={t('companyRegistration.seoDescription')} canonical="https://renest.fr/company-registration" />
      <div className="min-h-screen bg-gradient-subtle">
        <Header />
        <div className="container mx-auto py-16 px-4">
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl text-center">{t('companyRegistration.title')}</CardTitle>
              <CardDescription className="text-center">{t('companyRegistration.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">{t('companyRegistration.form.firstName')} *</Label>
                    <Input id="firstName" value={formData.firstName} onChange={(e) => setFormData(prev => ({...prev, firstName: e.target.value}))} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">{t('companyRegistration.form.lastName')} *</Label>
                    <Input id="lastName" value={formData.lastName} onChange={(e) => setFormData(prev => ({...prev, lastName: e.target.value}))} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyName">{t('companyRegistration.form.companyName')} *</Label>
                  <Input id="companyName" value={formData.companyName} onChange={(e) => setFormData(prev => ({...prev, companyName: e.target.value}))} required />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('companyRegistration.form.sector')} *</Label>
                    <Select value={formData.sector} onValueChange={(value) => setFormData(prev => ({...prev, sector: value}))} required>
                      <SelectTrigger><SelectValue placeholder={t('companyRegistration.form.sectorPlaceholder')} /></SelectTrigger>
                      <SelectContent>
                        {sectorKeys.map(key => <SelectItem key={key} value={t(`companyRegistration.sectors.${key}`)}>{t(`companyRegistration.sectors.${key}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('companyRegistration.form.employeeCount')} *</Label>
                    <Select value={formData.employeeCount} onValueChange={(value) => setFormData(prev => ({...prev, employeeCount: value}))} required>
                      <SelectTrigger><SelectValue placeholder={t('companyRegistration.form.employeeCountPlaceholder')} /></SelectTrigger>
                      <SelectContent>
                        {employeeCounts.map(count => <SelectItem key={count} value={count}>{count} {t('common.employees')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('companyRegistration.form.email')} *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))} placeholder={t('companyRegistration.form.emailPlaceholder')} required />
                  <p className="text-sm text-muted-foreground">{t('companyRegistration.form.emailHint')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t('companyRegistration.form.password')} *</Label>
                  <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))} placeholder={t('companyRegistration.form.passwordPlaceholder')} minLength={6} required />
                </div>
                <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                  {isLoading ? t('companyRegistration.form.submitting') : t('companyRegistration.form.submit')}
                </Button>
                <p className="text-sm text-muted-foreground text-center">{t('companyRegistration.form.disclaimer')}</p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default CompanyRegistration;
